import { FC, useMemo } from 'react'
import { Create, type RaRecord, useRecordContext, useRecordFromLocation } from 'react-admin'

import { specialEventDefaultStart } from '../../data'
import type { EventRecord } from '../../data/source'
import { BackButton } from '../molecules/BackButton'
import { SpecialEventForm } from '../molecules/SpecialEventForm'
import { useEventRecord, useScaleEvents } from '../molecules/useScaleEvents'

/** Back to the scale the event belongs to. */
export const toScale = (_resource?: string, _id?: unknown, data?: Partial<RaRecord>): string => `scales/${String(data?.scaleRecordId)}/show`

/**
 * Leaves the create or edit form for the event's scale, without saving. The
 * edited event is the record; a new one's scale comes with the router state.
 */
export const BackToScale: FC = () => {
  const fromLocation = useRecordFromLocation()
  const record = useRecordContext() ?? fromLocation
  if (typeof record?.scaleRecordId !== 'string') return null
  return <BackButton to={`/${toScale(undefined, undefined, record)}`} label="resources.scale_events.actions.back_to_scale" />
}

/**
 * A new event's start (see `specialEventDefaultStart`): now, to the minute,
 * when a berth stay is in progress, otherwise the next stay's start; empty
 * when the scale has no stay in progress or to come.
 */
export const defaultStart = (events: readonly EventRecord[]) => (): Record<string, unknown> => {
  const now = new Date()
  now.setSeconds(0, 0)
  const start = specialEventDefaultStart(events, now.getTime())
  return start === null ? {} : { startTime: new Date(start).toISOString() }
}

/** The form, once the scale's events are in, so the start can be prefilled from its stays. */
const PrefilledSpecialEventForm: FC = () => {
  const record = useEventRecord()
  const events = useScaleEvents(record?.scaleRecordId)
  // Stable: React Admin resets the form whenever its default values change.
  const defaultValues = useMemo(() => (events === undefined ? undefined : defaultStart(events)), [events])
  return defaultValues === undefined ? null : <SpecialEventForm defaultValues={defaultValues} />
}

/**
 * Opened from a scale's empty MARPOL/BOMBEO slot, which passes `{ scaleRecordId, scaleId, year, type }`
 * as the starting record through the router state.
 */
export const SpecialEventCreate = () => (
  <Create title="resources.scale_events.title_create" redirect={toScale} actions={<BackToScale />}>
    <PrefilledSpecialEventForm />
  </Create>
)
